import { useState } from 'react'
import { PageLayout } from '@/components/layout'
import { useFixedCostsRange } from '@/hooks/useFixedCostsRange'
import { useFixedCostSources } from '@/hooks/useFixedCostSources'
import FixedCostsTable from '@/components/costs/FixedCostsTable'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

export default function FixedCostsPage({ user, onLogout }) {
  const [range, setRange] = useState('12m')
  const tableData = useFixedCostsRange(range)
  const { syncCosts, isSyncing } = useFixedCostSources()

  const handleSyncXero = async () => {
    try {
      const result = await syncCosts(12)
      if (result?.months_updated > 0) {
        toast.success(`Synced ${result.sources_synced} sources — ${result.months_updated} months updated`)
      } else {
        toast.info('No new cost data from Xero')
      }
      tableData.refresh()
    } catch (err) {
      toast.error('Xero sync failed', { description: err.message })
    }
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Fixed Costs</h1>
            <p className="text-muted-foreground mt-1">
              Monthly operational expenses — click any cell to edit
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-input bg-background">
              <button
                onClick={() => setRange('12m')}
                className={`px-3 py-1.5 text-sm font-medium rounded-l-md transition-colors ${
                  range === '12m'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                12 months
              </button>
              <button
                onClick={() => setRange('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-r-md transition-colors ${
                  range === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                All time
              </button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncXero}
              disabled={isSyncing || tableData.isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync Xero'}
            </Button>
          </div>
        </div>

        <FixedCostsTable
          months={tableData.months}
          grid={tableData.grid}
          categories={tableData.categories}
          rowTotals={tableData.rowTotals}
          columnTotals={tableData.columnTotals}
          grandTotal={tableData.grandTotal}
          isLoading={tableData.isLoading}
          error={tableData.error}
          onRefresh={tableData.refresh}
        />
      </div>
    </PageLayout>
  )
}
